import { FC, useRef, useState } from "react";
import styled from "styled-components";

import Button from "@/components/Button";
import { Box } from "@/components/Dropdown";
import Text from "@/components/Text";
import { Input, Label } from "@/components/Forms";
import ProductsFilterDropdow from "./ProductsFilterDropdow";

const StyledBox = styled(Box)`
  width: 100%;
  padding: 26px 24px 32px;
`;

const FilterHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const FilterFooter = styled.div`
  text-align: right;
`;

const BodyHeaderLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  margin: 0 16px;
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.colors.errorLight};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    cursor: not-allowed;
    &:hover,
    &:active {
      border-color: transparent;
    }
  }
`;

const FilterGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 20px;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

interface Props {
  filter: {
    rating?: number;
    minPrice?: number;
    maxPrice?: number;
    minDiscount?: number;
    minReviews?: number;
    maxReviews?: number;
    isPrime?: boolean;
    isBestSeller?: boolean;
    isChoice?: boolean;
  };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      rating?: number;
      minPrice?: number;
      maxPrice?: number;
      minDiscount?: number;
      minReviews?: number;
      maxReviews?: number;
      isPrime?: boolean;
      isBestSeller?: boolean;
      isChoice?: boolean;
    }>
  >;
  closeFilter: () => void;
}

const ProductsFilterBox: FC<Props> = ({ filter, setFilter, closeFilter }) => {
  const ratingRef = useRef<HTMLInputElement>(null);
  const minPriceRef = useRef<HTMLInputElement>(null);
  const maxPriceRef = useRef<HTMLInputElement>(null);
  const minDiscountRef = useRef<HTMLInputElement>(null);
  const minReviewsRef = useRef<HTMLInputElement>(null);
  const maxReviewsRef = useRef<HTMLInputElement>(null);

  const [filterBooleanValues, setFilterBooleanValues] = useState({
    isPrime: filter.isPrime,
    isBestSeller: filter.isBestSeller,
    isChoice: filter.isChoice,
  });

  const onFilterApply = () => {
    setFilter({
      ...(ratingRef.current?.value && {
        rating: parseFloat(ratingRef.current?.value),
      }),
      ...(minPriceRef.current?.value && {
        minPrice: parseFloat(minPriceRef.current?.value),
      }),
      ...(maxPriceRef.current?.value && {
        maxPrice: parseFloat(maxPriceRef.current?.value),
      }),
      ...(minDiscountRef.current?.value && {
        minDiscount: parseFloat(minDiscountRef.current?.value),
      }),
      ...(minReviewsRef.current?.value && {
        minReviews: parseFloat(minReviewsRef.current?.value),
      }),
      ...(maxReviewsRef.current?.value && {
        maxReviews: parseFloat(maxReviewsRef.current?.value),
      }),
      isPrime: filterBooleanValues.isPrime,
      isBestSeller: filterBooleanValues.isBestSeller,
      isChoice: filterBooleanValues.isChoice,
    });
    closeFilter();
  };

  const onFilterReset = () => {
    setFilter({ rating: 4.3 });
    closeFilter();
  };

  return (
    <StyledBox>
      <FilterHeading>
        <Text $type="button">Filter</Text>
        <BodyHeaderLine />
        <ResetButton onClick={() => onFilterReset()}>
          <Text $color="error" $type="smallHeadline">
            Reset
          </Text>
        </ResetButton>
      </FilterHeading>
      <FilterGroup>
        <div>
          <Label htmlFor="minRating">Min Rating</Label>
          <Input
            id="minRating"
            type="number"
            ref={ratingRef}
            defaultValue={filter.rating}
            placeholder="Add number"
          />
        </div>
        <div>
          <Label htmlFor="minPrice">Min Price, $</Label>
          <Input
            id="minPrice"
            type="number"
            ref={minPriceRef}
            defaultValue={filter.minPrice ?? ""}
            placeholder="Add number"
          />
        </div>
        <div>
          <Label htmlFor="maxPrice">Max Price, $</Label>
          <Input
            type="number"
            id="maxPrice"
            ref={maxPriceRef}
            defaultValue={filter.maxPrice}
            placeholder="Add number"
          />
        </div>
        <div>
          <Label htmlFor="minDiscount">Min Discount, %</Label>
          <Input
            type="number"
            id="minDiscount"
            ref={minDiscountRef}
            defaultValue={filter.minDiscount}
            placeholder="Add number"
          />
        </div>
        <div>
          <Label htmlFor="minReviews">Min Reviews Count</Label>
          <Input
            type="number"
            id="minReviews"
            ref={minReviewsRef}
            defaultValue={filter.minReviews}
            placeholder="Add number"
          />
        </div>
        <div>
          <Label htmlFor="maxReviews">Max Reviews Count</Label>
          <Input
            type="number"
            id="maxReviews"
            ref={maxReviewsRef}
            defaultValue={filter.maxReviews}
            placeholder="Add number"
          />
        </div>
        <div>
          <ProductsFilterDropdow
            label={"Is Amazon Prime"}
            value={filterBooleanValues.isPrime}
            setValue={setFilterBooleanValues}
            name="isPrime"
          />
        </div>
        <div>
          <ProductsFilterDropdow
            label={"Is Best Seller"}
            value={filterBooleanValues.isBestSeller}
            setValue={setFilterBooleanValues}
            name="isBestSeller"
          />
        </div>
        <div>
          <ProductsFilterDropdow
            label={"Is Amazon Choice"}
            value={filterBooleanValues.isChoice}
            setValue={setFilterBooleanValues}
            name="isChoice"
          />
        </div>
      </FilterGroup>
      <FilterFooter>
        <StyledButton onClick={closeFilter} color="secondary">
          Cancel
        </StyledButton>
        <ApplyButton color="colorized" onClick={onFilterApply}>
          Apply filter
        </ApplyButton>
      </FilterFooter>
    </StyledBox>
  );
};

export default ProductsFilterBox;
